import { ContentType, ProgressForLessons, ProgressForCourses } from '@atlassian/learning-components';

import { CollectionType, ContentHubType } from 'src/constants';
import { LearningProgressStatus } from 'src/services/http/progress';
import { sendTrackEvent } from 'src/utils/analytics';
import { getCatalogUrl, getCollectionUrl, getHubUrl } from 'src/utils/getUrl';
import { getSlug } from 'src/utils/slug';

interface NavigationItem {
  sys: {
    id: string;
  };
  slug: string;
  lessonsCollection?: {
    items: {
      sys: { id: string };
      slug: string;
    }[];
  };
}

interface LessonProgressItem {
  lessonId: string;
  status: LearningProgressStatus;
  section: {
    sectionId: string;
    status: LearningProgressStatus;
  }[];
  [key: string]: string | LearningProgressStatus | { sectionId: string; status: LearningProgressStatus }[];
}

interface CourseProgressItem {
  courseId: string;
  status: LearningProgressStatus;
  [key: string]: string | LearningProgressStatus;
}

interface NavigationOptions {
  items: NavigationItem[];
  progress: ProgressForCourses | ProgressForLessons;
  itemIdField: string;
  contentSlug: string;
  contentType: ContentType.COURSE | ContentType.LEARNING_PATH;
  userId?: string;
  navigateToItem: (slug: string) => void;
  navigateToLesson?: (lessonSlug: string, courseSlug: string) => void;
  lessonsProgress?: ProgressForLessons;
}

interface GetNextItemOptions {
  items: NavigationItem[];
  progress: ProgressForCourses | ProgressForLessons;
  itemIdField: string;
  contentType: ContentType.COURSE | ContentType.LEARNING_PATH;
}

export const getNextItemSlug = ({ items, progress, itemIdField, contentType }: GetNextItemOptions): string => {
  if (!items?.length) {
    return '';
  }

  const hasNotStarted = !progress.overallStatus || progress.overallStatus === LearningProgressStatus.NOT_STARTED;

  const shouldNavigateToFirstItem = hasNotStarted || progress.overallStatus === LearningProgressStatus.COMPLETED;

  const firstItemSlug = getSlug(items[0]);

  if (shouldNavigateToFirstItem) {
    return firstItemSlug;
  }

  // For courses
  if (contentType === ContentType.COURSE) {
    const lessons = (progress as ProgressForLessons).lessons || [];

    for (const item of items) {
      const itemProgress = lessons.find((p) => (p as LessonProgressItem)[itemIdField] === item.sys.id);

      if (itemProgress?.status !== LearningProgressStatus.COMPLETED) {
        return getSlug(item);
      }
    }
  } else {
    // For learning paths
    const courses = (progress as ProgressForCourses).courses || [];
    const inProgressItem = items.find((item) =>
      courses.some(
        (p) =>
          (p as CourseProgressItem)[itemIdField] === item.sys.id && p.status === LearningProgressStatus.IN_PROGRESS,
      ),
    );

    if (inProgressItem) {
      return getSlug(inProgressItem);
    }

    const notStartedItem = items.find((item) =>
      courses.some(
        (p) =>
          (p as CourseProgressItem)[itemIdField] === item.sys.id && p.status === LearningProgressStatus.NOT_STARTED,
      ),
    );

    if (notStartedItem) {
      return getSlug(notStartedItem);
    }
  }

  // Fallback to first item
  return firstItemSlug;
};

const handleLearningPathNavigation = (
  items: NavigationItem[],
  progress: ProgressForCourses | ProgressForLessons,
  itemIdField: string,
  lessonsProgress: ProgressForLessons | undefined,
  navigateToItem: (slug: string) => void,
  navigateToLesson: (lessonSlug: string, courseSlug: string) => void,
): void => {
  const nextCourseSlug = getNextItemSlug({ items, progress, itemIdField, contentType: ContentType.LEARNING_PATH });
  const nextCourse = items.find((course) => getSlug(course) === nextCourseSlug);

  if (!nextCourse?.lessonsCollection?.items?.length) {
    navigateToItem(nextCourseSlug);

    return;
  }

  const lessonsProgressDefault = {
    overallStatus: lessonsProgress?.overallStatus || LearningProgressStatus.NOT_STARTED,
    lessons: lessonsProgress?.lessons || [],
  };

  const nextLessonSlug = getNextItemSlug({
    items: nextCourse.lessonsCollection.items,
    progress: lessonsProgressDefault,
    itemIdField: 'lessonId',
    contentType: ContentType.COURSE,
  });

  navigateToLesson(nextLessonSlug, getSlug(nextCourse));
};

export const navigateToNextItem = ({
  items,
  progress,
  itemIdField,
  contentSlug,
  contentType,
  userId,
  navigateToItem,
  navigateToLesson,
  lessonsProgress,
}: NavigationOptions): void => {
  if (!items?.length) {
    return;
  }

  const hasNotStarted = !progress.overallStatus || progress.overallStatus === LearningProgressStatus.NOT_STARTED;

  if (hasNotStarted) {
    sendTrackEvent(
      'learningApp',
      contentType === ContentType.COURSE ? 'course' : 'learningPath',
      'started',
      {
        [`${contentType === ContentType.COURSE ? 'course' : 'learningPath'}Slug`]: contentSlug,
      },
      userId,
    );
  }

  if (contentType === ContentType.LEARNING_PATH && navigateToLesson) {
    handleLearningPathNavigation(items, progress, itemIdField, lessonsProgress, navigateToItem, navigateToLesson);

    return;
  }

  const nextItemSlug = getNextItemSlug({ items, progress, itemIdField, contentType });

  navigateToItem(nextItemSlug);
};

export const subnavbarPrimaryItems = [
  {
    title: 'Get started',
    description: 'Learn the basics to get started with Atlassian.',
    link: getHubUrl(ContentHubType.LEARNING_PATHS, 'get-started-quickly'),
  },
  {
    title: 'Grow your career',
    description: 'Level up your career with role-focused learning.',
    link: getHubUrl(ContentHubType.ROLE_BASED),
  },
  {
    title: 'Get certification ready',
    description: 'Everything you need to prep for your exam.',
    link: getHubUrl(ContentHubType.CERT_PREP),
  },
  {
    title: 'Build admin skills',
    description: 'Learn advanced skills to become an expert admin.',
    link: getHubUrl(ContentHubType.LEARNING_PATHS, 'build-admin-skills'),
  },
];

export const subnavbarSecondaryItems = [
  {
    title: 'By app',
    links: [
      {
        text: 'Jira',
        href: getCatalogUrl('?product=Jira'),
      },
      {
        text: 'Confluence',
        href: getCatalogUrl('?product=Confluence'),
      },
      {
        text: 'Loom',
        href: getCatalogUrl('?product=Loom'),
      },
      {
        text: 'Rovo',
        href: getCatalogUrl('?product=Rovo'),
      },
      {
        text: 'Jira Service Management',
        href: getCatalogUrl('?product=Jira+Service+Management'),
      },
      {
        text: 'Trello',
        href: getCatalogUrl('?product=Trello'),
      },
      {
        text: 'Jira Product Discovery',
        href: getCatalogUrl('?product=Jira+Product+Discovery'),
      },
      {
        text: 'Forge',
        href: getCatalogUrl('?product=Forge'),
      },
    ],
  },
  {
    title: 'By type',
    links: [
      {
        text: 'Learning paths',
        href: getHubUrl(ContentHubType.LEARNING_PATHS),
      },
      {
        text: 'Collections',
        href: getCatalogUrl('?type=Collection'),
      },
    ],
  },
  {
    title: 'By subject',
    links: [
      {
        text: 'Cloud migration',
        href: getCollectionUrl(CollectionType.TOPIC, 'transition-to-atlassian-cloud'),
      },
      {
        text: 'Teamwork practices',
        href: getHubUrl(ContentHubType.LEARNING_PATHS, 'boost-productivity-and-teamwork'),
      },
    ],
  },
];
